import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { posthogTrackPageView, posthogSetUserProps, NavigationEvents, posthogTrackEvent } from '../assets/posthog';

export const usePostHogTracking = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth0();

  // Track page views and route changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.posthog) {
      const pageName = getPageName(location.pathname);
      posthogTrackPageView(pageName, {
        path: location.pathname,
        search: location.search,
        hash: location.hash,
      });

      posthogTrackEvent({
        event: NavigationEvents.PAGE_VIEW,
        properties: {
          page: pageName,
          path: location.pathname,
          search: location.search,
          hash: location.hash,
        },
      });

      posthogTrackEvent({
        event: NavigationEvents.ROUTE_CHANGE,
        properties: {
          to: location.pathname,
          page: pageName,
          search: location.search,
          hash: location.hash,
          timestamp: new Date().toISOString(),
        },
      });
    }
  }, [location]);

  // Identify user when authenticated
  useEffect(() => {
    if (isAuthenticated && user && typeof window !== 'undefined' && window.posthog) {
      posthogSetUserProps({
        id: user.sub,
        email: user.email,
        name: user.name,
        nickname: user.nickname,
        picture: user.picture,
      });
    }
  }, [isAuthenticated, user]);
};

// Helper function to extract meaningful page names from paths
const getPageName = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  
  if (segments.length === 0) return 'home';
  
  // Map common routes to readable names
  const routeMap: Record<string, string> = {
    'explore': 'Explore',
    'simulator': 'Simulator',
    'simulators': 'Simulators',
    'knowledge-gaps': 'Knowledge Gaps',
    'knowledge-articles': 'Knowledge Articles',
    'reports': 'Reports',
    'quality': 'Quality',
    'integrations': 'Integrations',
    'onboarding': 'Onboarding',
    'training-center': 'Training Center',
    'copilot': 'Copilot',
    'playground': 'Playground',
    'trackers': 'Trackers',
    'skills': 'Skills',
    'regression-sets': 'Regression Sets',
    'regression-tests': 'Regression Tests',
    'automation-configuration': 'Automation Configuration',
  };

  const mainRoute = segments[0];
  return routeMap[mainRoute] || mainRoute.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};